import { BrregEnhet, getOrganizationAddress } from "@cappelendamm/brreg";
import {
  CUSTOMER_ADDRESS_METADATA,
  CUSTOMER_METADATA,
  CustomerType,
  ORGANIZATION_CUSTOMER_GROUP,
  OrganizationType,
  SCHOOL_ORGANIZATION_STATUS,
  type CreateOrganizationCustomerInput,
} from "@cappelendamm/e-commerce-services";

export enum OrganizationCreationError {
  ORGANIZATION_ALREADY_EXISTS = "ORGANIZATION_ALREADY_EXISTS",
  BRREG_API_ERROR = "BRREG_API_ERROR",
  ORGANIZATION_DATA_NOT_FOUND = "ORGANIZATION_DATA_NOT_FOUND",
  USER_SESSION_NOT_FOUND = "USER_SESSION_NOT_FOUND",
  CRYSTALLIZE_ORGANIZATION_CREATION_FAILED = "CRYSTALLIZE_ORGANIZATION_CREATION_FAILED",
}

export enum OrganizationRole {
  ADMIN = "org:admin",
  MEMBER = "org:member",
  STUDENT = "org:student",
}

/**
 * Builds a Crystallize customer input from Brreg organization data.
 * Callers can layer additional meta (e.g. school status, EHF billing)
 * on top.
 */
export function buildOrganizationCustomerInput({
  organization,
  orgNumber,
  email,
  canUseEHF = false,
  isTeachingInstitution = false,
}: {
  organization: BrregEnhet;
  orgNumber: string;
  email?: string;
  canUseEHF?: boolean;
  isTeachingInstitution?: boolean;
}): CreateOrganizationCustomerInput {
  const { address, postalCode, city } = getOrganizationAddress(organization);
  const baseAddress = {
    street: address || "",
    postalCode: postalCode || "",
    city: city || "",
    country: "NORGE",
    ...(email && { email }),
  };
  const companyName = organization.navn;

  return {
    type: CustomerType.ORGANIZATION,
    identifier: orgNumber,
    companyName,
    ...(email && { email }),
    parents: [
      {
        type: "customerGroup",
        identifier: ORGANIZATION_CUSTOMER_GROUP.ORGANIZATIONS,
      },
    ],
    meta: [
      { key: CUSTOMER_METADATA.ORGANIZATION_NUMBER, value: orgNumber },
      {
        key: CUSTOMER_METADATA.ORGANIZATION_TYPE,
        value: OrganizationType.COMPANY,
      },
      ...(isTeachingInstitution
        ? [
            {
              key: CUSTOMER_METADATA.SCHOOL_CUSTOMER_STATUS,
              value: SCHOOL_ORGANIZATION_STATUS.PENDING_APPROVAL,
            },
          ]
        : []),
    ],
    addresses: {
      delivery: baseAddress,
      billing: {
        ...baseAddress,
        meta: [
          {
            key: CUSTOMER_ADDRESS_METADATA.INVOICE_ORGANIZATION_NUMBER,
            value: orgNumber,
          },
          {
            key: CUSTOMER_ADDRESS_METADATA.USE_EHF,
            value: canUseEHF ? "true" : "false",
          },
        ],
      },
    },
  };
}
